/**
 * Pending-SELECT registry — "the row the dialog must seed from is still being
 * written".
 *
 * The rating dialog closes on the Select CLICK (parity: legacy fires
 * `rating_mode:hide` + `self.remove()` before the save chain, so the user is
 * never made to wait on it — coil.selector.js:3863-3870). The writes then run
 * on, detached, for as long as the section PATCH + the `/select` POST take.
 *
 * The chassis mounts a FRESH selector on every open (`key={dialog.openSeq}`,
 * ComponentSelectorDialog.tsx:546), so a reopen inside that window re-seeds
 * from a GET that can legitimately return the PRE-select row — the dialog then
 * renders no rated output at all, because "rated" is `capacity_total != null`
 * on a row the server has not written yet.
 *
 * Legacy has no equivalent hole: its Backbone models live in memory on the
 * board and `model.save(attrs)` sets `attrs` locally on the way past, so a
 * reopened dialog reads the just-selected values whether or not the XHR has
 * landed.
 *
 * This is the React-shaped equivalent of that guarantee — the selector waits
 * for the writes it must not race, instead of mirroring their payloads into the
 * RTKQ cache. A payload mirror is an allow-list and it rots: it cannot carry
 * nested serializer output (`master_coil`) or anything a serializer computes,
 * and it silently omits every field added later. Waiting has neither failure
 * mode, and the wait is spent on a preloader the dialog already shows while it
 * seeds.
 *
 * The promise NEVER rejects and always settles, so a caller can await it
 * unguarded (see `SETTLE_TIMEOUT_MS`).
 */

/** Belt-and-braces: a hung request must not pin a dialog's preloader for ever. */
const SETTLE_TIMEOUT_MS = 15_000;

/** key (`'<plural>:<id>'`) → the in-flight select chain for that row. */
const pending = new Map<string, Promise<void>>();

/** `'coils:1234'` — one namespace per detail row, so a Select on one component
 * never makes another component's dialog wait. */
export const selectWriteKey = (plural: string, id: number): string => `${plural}:${id}`;

/**
 * Register the write chain of a Select. `work` is awaited but never observed
 * for its value or its failure: a failed save leaves the dialog to seed from
 * whatever the server does hold, which is the same thing it would have shown
 * had the save never been attempted.
 */
export function trackSelectWrites(key: string, work: Promise<unknown>): void {
  let done: () => void;
  const settled = new Promise<void>((resolve) => {
    done = resolve;
  });
  const finish = () => {
    // only clear our own entry — a second Select on the same row registers a
    // newer chain and owns the slot from then on.
    if (pending.get(key) === settled) pending.delete(key);
    done();
  };
  const timer = window.setTimeout(finish, SETTLE_TIMEOUT_MS);
  void work
    .catch(() => undefined)
    .then(() => {
      window.clearTimeout(timer);
      finish();
    });
  pending.set(key, settled);
}

/**
 * The in-flight select chain for `key`, or null when there is nothing to wait
 * for — which is the case on every open that does not immediately follow a
 * Select, i.e. the overwhelming majority.
 */
export function pendingSelectWrites(key: string): Promise<void> | null {
  return pending.get(key) ?? null;
}
